/**
 * On-demand Google Font loader for the Media Studio text overlay.
 *
 * WHY: the text-overlay font picker offers 24 families, but `index.css` only
 * eager-loads 4 (Inter, DM Sans, Sora, Space Grotesk) — the
 * other 19 (Montserrat, Playfair Display, Poppins, Oswald, …) silently fell back
 * to a system font, so a brand could NOT reliably use its own font, and it never
 * rendered the same across machines/exports. Eager-loading all 24 is render-
 * blocking, so instead we load the chosen family lazily and WAIT for it to be
 * ready (Konva rasterizes text — the font must be available before the node
 * paints, or it captures the fallback glyphs).
 *
 * Brand-consistency note: pair this with `brand_kit.font_primary` so a campaign's
 * headline font is both pinned AND actually loaded. See CAMPAIGN-SYSTEM-AUDIT R-1.
 */

// Families the picker offers. Keep in sync with TextOverlayToolbar FONTS.
export const OVERLAY_FONT_FAMILIES = [
  'Inter',
  'Playfair Display',
  'Merriweather',
  'Space Grotesk',
  'Roboto',
  'Open Sans',
  'Lato',
  'Montserrat',
  'Oswald',
  'Source Sans 3',
  'Raleway',
  'Poppins',
  'Nunito',
  'Ubuntu',
  'Rubik',
  'Lora',
  'Work Sans',
  'Fira Sans',
  'Quicksand',
  'Barlow',
  'DM Sans',
  'IBM Plex Sans',
  'Mukta',
  'PT Sans',
] as const;

// Families already eager-loaded in index.css — no fetch needed.
//
// JetBrains Mono LEFT this set on 2026-09-07 when its `@import` was removed
// (founder: "we shouldn't have JetBrains Mono, just the one default font").
// Membership here means "do not fetch, it is already loaded" — so leaving a
// no-longer-eager family in it would make the picker skip the fetch for a font
// that is not there, and Konva would rasterize the fallback glyphs into the
// export. Stale membership here fails silently and permanently.
const PRELOADED = new Set(['Inter', 'DM Sans', 'Sora', 'Space Grotesk']);

const DEFAULT_WEIGHTS = [400, 500, 600, 700, 800];
const injected = new Set<string>();
const ready = new Map<string, Promise<void>>();

function familyId(family: string): string {
  return `gf-${family.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
}

/**
 * Ensure a Google font family is loaded and ready to render. Idempotent + cached.
 * Resolves immediately for preloaded families and is a no-op outside the browser.
 */
export async function loadGoogleFont(
  family: string,
  weights: number[] = DEFAULT_WEIGHTS,
): Promise<void> {
  if (!family || PRELOADED.has(family)) return;
  if (typeof document === 'undefined') return; // SSR / non-DOM
  if (ready.has(family)) return ready.get(family);

  const p = (async () => {
    const id = familyId(family);
    if (!injected.has(family) && !document.getElementById(id)) {
      injected.add(family);
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href =
        `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, '+')}` +
        `:wght@${weights.join(';')}&display=swap`;
      // Wait for the @font-face rules to actually be parsed before fonts.load() —
      // otherwise the load() promise can resolve against the not-yet-registered
      // face and the node rasterizes the fallback. Cap the wait so we never hang.
      const linkReady = new Promise<void>((resolve) => {
        link.onload = () => resolve();
        link.onerror = () => resolve();
        setTimeout(resolve, 4000);
      });
      document.head.appendChild(link);
      await linkReady;
    }
    // Wait for the actual font files so Konva rasterizes the real glyphs.
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    if (fonts?.load) {
      try {
        await Promise.all(weights.map((w) => fonts.load(`${w} 16px "${family}"`)));
      } catch {
        /* best-effort — fall through */
      }
    }
  })();

  ready.set(family, p);
  return p;
}
